'use client'

import type React from 'react'

import {
  Label,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart as RadialBarChartRecharts,
} from 'recharts'

import { cn } from '@/lib/cn'

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../chart'

export type RadialBarChartProps = {
  id?: string
  config: ChartConfig
  data: {
    label: string
    [key: string]: number | string
  }[]
  label?: {
    text: string
    value: string
  }
  tooltip?: boolean
  tooltipValueFormatter?: (value: string) => string
  className?: string
}

export const RadialBarChart: React.FC<RadialBarChartProps> = ({
  id,
  config,
  data,
  label,
  tooltip = true,
  tooltipValueFormatter,
  className,
}) => {
  return (
    <ChartContainer
      id={id}
      config={config}
      className={cn('h-auto min-h-24 w-full max-w-52', className)}
    >
      <RadialBarChartRecharts
        data={data}
        endAngle={180}
        innerRadius={80}
        outerRadius={160}
        cy="100%"
      >
        {tooltip && (
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                valueFormatter={tooltipValueFormatter}
                hideLabel
              />
            }
          />
        )}
        <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
          {label && (
            <Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) - 16}
                        className="fill-foreground text-2xl font-bold"
                      >
                        {label.value}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 4}
                        className="fill-muted-foreground"
                      >
                        {label.text}
                      </tspan>
                    </text>
                  )
                }
              }}
            />
          )}
        </PolarRadiusAxis>
        {data.map((value) => {
          const keys = Object.keys(value).filter((key) => key !== 'label')
          return keys.map((key) => {
            const colorConfig = config[key]

            if (colorConfig?.chartClassName) {
              return (
                <RadialBar
                  key={key}
                  dataKey={key}
                  stackId={value.label}
                  cornerRadius={4}
                  className={cn(
                    'stroke-transparent stroke-2',
                    colorConfig.chartClassName,
                  )}
                />
              )
            }

            if ('color' in colorConfig && colorConfig.color) {
              return (
                <RadialBar
                  key={key}
                  dataKey={key}
                  stackId={value.label}
                  fill={colorConfig.color}
                  cornerRadius={4}
                  className={cn('stroke-transparent stroke-2')}
                />
              )
            }

            return (
              <RadialBar
                key={key}
                dataKey={key}
                stackId={value.label}
                fill={`var(--color-${key})`}
                cornerRadius={4}
                className={cn('stroke-transparent stroke-2')}
              />
            )
          })
        })}
      </RadialBarChartRecharts>
    </ChartContainer>
  )
}
